import { Box, Button, Flex, Group, Loader, Menu, Table, Text } from '@mantine/core';
import { IconArrowRight, IconDots } from '@tabler/icons-react';
import React, { useCallback } from 'react';
import { ReactElement } from 'react';
import { NavLink } from 'react-router-dom';
import dataCaptureClasses from '../../DataCapture.module.css';
import { UploadedFile } from '../../hooks/file-uploads/types';
import FileNameWithIcon from './FileNameWithIcon';
import classes from './FilesTable.module.css';
import { useFileDragDrop } from '../context/FileDragDropContext';
import { createDragElement } from '../utils/createDragElement';
import { formatDateTime } from 'src/meetings/utils/date-utils';

type TableData = UploadedFile & {
  isGenerated?: boolean;
};

type PrimaryActionType<T> = {
  label: string;
  variant?: 'primary' | 'secondary';
  icon?: React.ComponentType<{ size?: number | string }>;
  condition?: (entityWithFile: T) => boolean;
} & (
  | { type: 'link'; getLink: (entityWithFile: T) => string; onClick?: (entityWithFile: T) => void }
  | { type: 'action'; onClick: (entityWithFile: T) => void }
);

type Props<T extends TableData> = {
  entitiesWithFile: T[];
  isLoading: boolean;
  onClick?: (entityWithFile: T) => void;
  additionalColumns?: {
    label: string;
    valueBuilder: (f: T) => ReactElement;
  }[];
  primaryAction?: PrimaryActionType<T> | ((entityWithFile: T) => PrimaryActionType<T> | undefined);
  actions?: Action<T>[] | ((entityWithFile: T) => Action<T>[]);
  enableDragDrop?: boolean;
};

type Action<T> = ActionItem<T> | Divider;

type Divider = {
  type: 'divider';
};

type ActionItem<T> = {
  label: string;
  action: (entityWithFile: T, anchor?: HTMLAnchorElement) => unknown;
  condition?: (entityWithFile: T) => boolean;
};

function isDivider<T>(action: Action<T>): action is Divider {
  return 'type' in action && action.type === 'divider';
}
export default function FilesTable<T extends TableData>({
  entitiesWithFile,
  isLoading,
  primaryAction,
  additionalColumns = [],
  onClick = () => {},
  actions = [],
  enableDragDrop = false,
}: Props<T>) {
  const rows = entitiesWithFile.map(entityWithFile => (
    <FileRow
      key={entityWithFile.id}
      entityWithFile={entityWithFile}
      primaryAction={primaryAction}
      onClick={onClick}
      additionalColumns={additionalColumns}
      actions={actions}
      enableDragDrop={enableDragDrop}
    />
  ));

  const headers = (
    <Table.Tr>
      <Table.Th>Name</Table.Th>
      <Table.Th>Date uploaded</Table.Th>
      {additionalColumns.map(c => (
        <Table.Th key={c.label}>{c.label}</Table.Th>
      ))}
      <Table.Th>Actions</Table.Th>
    </Table.Tr>
  );

  return (
    <Box>
      <Table
        variant="bare"
        captionSide="bottom"
        classNames={{ th: dataCaptureClasses.th }}
        verticalSpacing={'sm'}
      >
        <Table.Thead>{headers}</Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>
      {isLoading && (
        <Flex justify={'center'} mt={'md'}>
          <Loader />
        </Flex>
      )}
    </Box>
  );
}

type RowProp<T extends TableData> = {
  entityWithFile: T;
  primaryAction?: PrimaryActionType<T> | ((entityWithFile: T) => PrimaryActionType<T> | undefined);
  onClick: (entityWithFile: T) => void;
  additionalColumns?: {
    label: string;
    valueBuilder: (f: T) => ReactElement;
  }[];
  actions?: Action<T>[] | ((entityWithFile: T) => Action<T>[]);
  enableDragDrop?: boolean;
};

const FileRow = <T extends TableData>({
  entityWithFile,
  primaryAction,
  onClick,
  actions,
  additionalColumns = [],
  enableDragDrop = false,
}: RowProp<T>) => {
  const resolvedActions = typeof actions === 'function' ? actions(entityWithFile) : actions;
  const dragDropContext = useFileDragDrop();
  const handleRowClick = useCallback(
    (e: React.MouseEvent) => {
      if (dragDropContext?.draggedItem) {
        e.preventDefault();
        return;
      }
      onClick(entityWithFile);
    },
    [onClick, entityWithFile, dragDropContext?.draggedItem],
  );

  const handleMenuIconClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleMenuItemClick = useCallback(
    (action: (entityWithFile: T, anchor?: HTMLAnchorElement) => unknown) => {
      return () => {
        const a = document.createElement('a');
        action(entityWithFile, a);
      };
    },
    [entityWithFile],
  );

  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      if (dragDropContext) {
        e.dataTransfer.effectAllowed = 'move';
        dragDropContext.setDraggedItem(entityWithFile);
        createDragElement(entityWithFile, e);
      }
    },
    [dragDropContext, entityWithFile],
  );

  const handleDragEnd = useCallback(() => {
    if (dragDropContext) {
      dragDropContext.setDraggedItem(null);
      dragDropContext.setIsDraggedOver(null);
    }
  }, [dragDropContext]);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (
        dragDropContext &&
        entityWithFile.isFolder &&
        dragDropContext.canDropOnTarget(entityWithFile.id)
      ) {
        e.preventDefault();
        dragDropContext.setIsDraggedOver(entityWithFile.id);
      }
    },
    [dragDropContext, entityWithFile],
  );

  const handleDragLeave = useCallback(() => {
    if (dragDropContext) {
      dragDropContext.setIsDraggedOver(null);
    }
  }, [dragDropContext]);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      if (dragDropContext && entityWithFile.isFolder && dragDropContext.draggedItem) {
        try {
          await dragDropContext.handleMoveFile(dragDropContext.draggedItem, entityWithFile.id);
        } finally {
          dragDropContext.setDraggedItem(null);
          dragDropContext.setIsDraggedOver(null);
        }
      }
    },
    [dragDropContext, entityWithFile],
  );

  const isDragging = dragDropContext?.isDragging(entityWithFile.id);
  const isDropTarget = dragDropContext?.isDropTarget(entityWithFile.id);
  const draggable = enableDragDrop && !!dragDropContext;

  const getRowClassName = () => {
    let className = '';

    if (entityWithFile.isFolder) {
      className += classes.folderRow;
    }

    if (draggable) {
      className += ` ${classes.draggableRow}`;

      if (isDragging) {
        className += ` ${classes.dragging}`;
      }

      if (isDropTarget && entityWithFile.isFolder) {
        className += ` ${classes.dropTarget}`;
      }
    }

    return className.trim();
  };

  return (
    <Table.Tr
      key={entityWithFile.id}
      onClick={handleRowClick}
      className={getRowClassName()}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Table.Td>
        <FileNameWithIcon
          isFolder={Boolean(entityWithFile.isFolder)}
          isGenerated={Boolean(entityWithFile.isGenerated)}
          fileName={entityWithFile.file.fileName}
          fileSize={entityWithFile.file.fileSize}
        />
      </Table.Td>
      <Table.Td>
        <Text fw={400} size="sm">
          {formatDateTime(entityWithFile.file.createdAt)}
        </Text>
      </Table.Td>
      {additionalColumns.map(c => (
        <Table.Td key={c.label}>{c.valueBuilder(entityWithFile)}</Table.Td>
      ))}
      <Table.Td align={'right'}>
        <Group justify={'space-between'} wrap={'nowrap'}>
          <PrimaryActionButton primaryAction={primaryAction} entityWithFile={entityWithFile} />
          <Menu
            transitionProps={{ transition: 'pop-top-right' }}
            position="bottom-end"
            width={220}
            withinPortal
          >
            <Menu.Target>
              <IconDots color={'#475467'} stroke={2} size={20} onClick={handleMenuIconClick} />
            </Menu.Target>
            <Menu.Dropdown>
              {resolvedActions?.map((a, i) => {
                if (isDivider(a)) {
                  return <Menu.Divider key={i} />;
                }
                if (a.condition && !a.condition(entityWithFile)) {
                  return null;
                }
                return (
                  <Menu.Item
                    key={i}
                    onClick={e => {
                      e.stopPropagation();
                      handleMenuItemClick(a.action)();
                    }}
                  >
                    {a.label}
                  </Menu.Item>
                );
              })}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};

type PrimaryActionButtonProps<T extends TableData> = {
  primaryAction?: PrimaryActionType<T> | ((entityWithFile: T) => PrimaryActionType<T> | undefined);
  entityWithFile: T;
};

const PrimaryActionButton = <T extends TableData>({
  primaryAction,
  entityWithFile,
}: PrimaryActionButtonProps<T>) => {
  const resolvedPrimaryAction =
    typeof primaryAction === 'function' ? primaryAction(entityWithFile) : primaryAction;

  const handleActionClick = useCallback(() => {
    if (resolvedPrimaryAction?.type === 'action') {
      resolvedPrimaryAction.onClick(entityWithFile);
    }
  }, [resolvedPrimaryAction, entityWithFile]);

  if (!resolvedPrimaryAction) {
    return <Box />;
  }

  const isDisabled =
    resolvedPrimaryAction.condition && !resolvedPrimaryAction.condition(entityWithFile);
  const variant = resolvedPrimaryAction.variant || 'primary';
  const IconComponent = resolvedPrimaryAction.icon || IconArrowRight;

  if (resolvedPrimaryAction.type === 'link') {
    return (
      <Button
        variant={variant}
        component={NavLink}
        disabled={isDisabled}
        onClick={() => {
          if (resolvedPrimaryAction.onClick) {
            resolvedPrimaryAction.onClick(entityWithFile);
          }
        }}
        to={resolvedPrimaryAction.getLink(entityWithFile)}
        rightSection={<IconComponent size={16} />}
      >
        {resolvedPrimaryAction.label}
      </Button>
    );
  }

  return (
    <Button
      variant={variant}
      disabled={isDisabled}
      onClick={e => {
        e.stopPropagation();
        handleActionClick();
      }}
      rightSection={<IconComponent size={16} />}
    >
      {resolvedPrimaryAction.label}
    </Button>
  );
};
